import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "app/store";
import { MARKETING } from "constants/api";
import axios from "utils/http";

export interface IMarketingOverviewResponse extends ApiResponse {
  data: IMarketingOverview;
}

export const OVERVIEW_LOAD_ERROR = "Could not load the marketing overview. Please try again.";

// Validate the fields rendered by the overview before treating the request as successful.
const numericFields: (keyof IMarketingOverview)[] = [
  "partners_hold_kobo", "live_partners", "unspent_kobo", "active_vouchers",
  "prepaid_not_issued_kobo", "drafts", "awaiting_funding", "branches",
  "issued_kobo", "issued_count", "redeemed_kobo", "redemption_count",
  "expired_unspent_kobo", "emails_not_delivered", "batch_count", "device_count",
  "low_cover_count", "ledger_entries",
];

const isOverview = (value: unknown): value is IMarketingOverview => {
  if (!value || typeof value !== "object") return false;
  const data = value as Record<string, unknown>;
  return numericFields.every((key) => typeof data[key] === "number" && Number.isFinite(data[key])) &&
    typeof data.ledger === "string" &&
    typeof data.ledger_checked_at === "string" &&
    (data.lowest_cover_partner === null || typeof data.lowest_cover_partner === "string") &&
    Array.isArray(data.needs_attention) &&
    data.needs_attention.every((item: unknown) => {
      if (!item || typeof item !== "object") return false;
      const row = item as Record<string, unknown>;
      return typeof row.partner_id === "number" && Number.isFinite(row.partner_id) &&
        typeof row.type === "string" && typeof row.label === "string" &&
        typeof row.since === "string";
    });
};

export const fetchMarketingOverview = createAsyncThunk<
  IMarketingOverview,
  ReduxAction,
  { state: RootState; rejectValue: string }
>(
  MARKETING.OVERVIEW,
  async ({ onFailure }, { rejectWithValue }) => {
    try {
      const response: IMarketingOverviewResponse = await axios.get(MARKETING.OVERVIEW);
      if (response.status === false || !isOverview(response.data)) {
        throw new Error(OVERVIEW_LOAD_ERROR);
      }
      return response.data;
    } catch (error) {
      onFailure?.(error as IErrorResponse);
      const forbidden = (error as { isForbidden?: boolean } | null)?.isForbidden;
      return rejectWithValue(forbidden
        ? "You do not have permission to view the marketing overview."
        : OVERVIEW_LOAD_ERROR);
    }
  },
  {
    condition: (_, { getState }) => !getState().marketingOverview.isLoading,
  }
);
